import { useState, useEffect } from "react";
import { X, Keyboard } from "lucide-react";
import { ALL_SHORTCUTS, getShortcutDisplay } from "../hooks/useKeyboardShortcuts";

export function KeyboardShortcutsModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleShow = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    window.addEventListener("show-keyboard-shortcuts", handleShow);
    window.addEventListener("close-modal", handleClose);

    return () => {
      window.removeEventListener("show-keyboard-shortcuts", handleShow);
      window.removeEventListener("close-modal", handleClose);
    };
  }, []);

  if (!isOpen) return null;

  const navigationShortcuts = ALL_SHORTCUTS.filter(
    (s) => s.description.startsWith("Go to")
  );
  const actionShortcuts = ALL_SHORTCUTS.filter(
    (s) =>
      !s.description.startsWith("Go to") &&
      s.key !== "Escape" &&
      s.key !== "?"
  );
  const otherShortcuts = ALL_SHORTCUTS.filter(
    (s) => s.key === "Escape" || s.key === "?"
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Keyboard className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Navigation */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Navigation
            </h3>
            <div className="space-y-2">
              {navigationShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key}
                  shortcut={shortcut}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Actions
            </h3>
            <div className="space-y-2">
              {actionShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key}
                  shortcut={shortcut}
                />
              ))}
            </div>
          </div>

          {/* Other */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
              General
            </h3>
            <div className="space-y-2">
              {otherShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key + (shortcut.shift ? "-shift" : "")}
                  shortcut={shortcut}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <p className="text-sm text-slate-500 text-center">
            Press <kbd className="px-2 py-1 bg-slate-200 rounded text-xs font-mono">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
}

function ShortcutRow({ shortcut }: { shortcut: { key: string; shift?: boolean; description: string } }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg transition-colors">
      <span className="text-sm text-slate-700">{shortcut.description}</span>
      <kbd className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-mono text-slate-600 shadow-sm">
        {getShortcutDisplay(shortcut as any)}
      </kbd>
    </div>
  );
}
